import { CityEnum } from "../city";
import { useState, type ChangeEvent } from "react";
import { isValidEmail } from "../utils/emailValidator";
import { isValidPhoneNumber } from "../utils/phoneValidator";
import { formatPhoneNumber } from "../utils/formatPhoneNumber";
import {
  Box,
  Button,
  Dialog,
  TextField,
  Typography,
  DialogTitle,
  Autocomplete,
  DialogActions,
  DialogContent
} from "@mui/material";

interface AddAgentModalProps {
  openDialog: boolean;
  setOpenDialog: (open: boolean) => void;
}

export const AddAgentModal = ({
  openDialog,
  setOpenDialog
}: AddAgentModalProps) => {
  const [formValues, setFormValues] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    phoneNumber: "",
    emailAddress: "",
    cities: [] as string[]
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    emailAddress: "",
    cities: ""
  });

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormValues({
      firstName: "",
      middleName: "",
      lastName: "",
      phoneNumber: "",
      emailAddress: "",
      cities: []
    });
    setErrors({
      firstName: "",
      lastName: "",
      phoneNumber: "",
      emailAddress: "",
      cities: ""
    });
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const formattedValue =
      name === "phoneNumber" ? formatPhoneNumber(value) : value;

    setFormValues((prev) => ({ ...prev, [name]: formattedValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleAddAgent = () => {
    const newErrors = {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      emailAddress: "",
      cities: ""
    };
    let isValid = true;

    if (!formValues.firstName.trim()) {
      newErrors.firstName = "First name is required.";
      isValid = false;
    }

    if (!formValues.lastName.trim()) {
      newErrors.lastName = "Last name is required.";
      isValid = false;
    }

    let isPhoneValid = false;
    try {
      isPhoneValid =
        isValidPhoneNumber(formValues.phoneNumber, "US") ||
        isValidPhoneNumber(formValues.phoneNumber, "CA");
    } catch (error) {
      isPhoneValid = false;
    }

    if (!isPhoneValid) {
      newErrors.phoneNumber = "Invalid phone number.";
      isValid = false;
    }

    if (!formValues.emailAddress.trim()) {
      newErrors.emailAddress = "Email address is required.";
      isValid = false;
    } else if (!isValidEmail(formValues.emailAddress)) {
      newErrors.emailAddress = "Enter a valid email address.";
      isValid = false;
    }

    if (formValues.cities.length === 0) {
      newErrors.cities = "At least one city must be selected.";
      isValid = false;
    }

    setErrors(newErrors);

    if (isValid) {
      console.log("Validated Agent Data:", formValues);
      handleCloseDialog();
    }
  };

  return (
    <Dialog
      open={openDialog}
      onClose={handleCloseDialog}
      fullWidth
      maxWidth="sm"
      aria-labelledby="add-agent-dialog-title"
    >
      <DialogTitle
        id="add-agent-dialog-title"
        sx={{
          fontWeight: "bold",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        Add New Agent
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Please provide the following information to add a new agent. Fields
          marked with <span style={{ color: "red" }}>*</span> are required.
        </Typography>
        <Box
          component="form"
          noValidate
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2
          }}
        >
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={formValues.firstName}
            onChange={handleInputChange}
            error={!!errors.firstName}
            helperText={errors.firstName}
            variant="outlined"
            required
          />
          <TextField
            fullWidth
            label="Middle Name"
            name="middleName"
            value={formValues.middleName}
            onChange={handleInputChange}
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={formValues.lastName}
            onChange={handleInputChange}
            error={!!errors.lastName}
            helperText={errors.lastName}
            variant="outlined"
            required
          />
          <TextField
            fullWidth
            label="Phone Number"
            name="phoneNumber"
            value={formValues.phoneNumber}
            onChange={handleInputChange}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber}
            variant="outlined"
            required
          />
          <TextField
            fullWidth
            label="Email Address"
            name="emailAddress"
            value={formValues.emailAddress}
            onChange={handleInputChange}
            error={!!errors.emailAddress}
            helperText={errors.emailAddress}
            variant="outlined"
            required
          />
          <Autocomplete
            multiple
            options={Object.values(CityEnum)}
            getOptionLabel={(option) => option}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cities"
                variant="outlined"
                required
                error={!!errors.cities}
                helperText={errors.cities}
              />
            )}
            value={formValues.cities}
            onChange={(_, value) => {
              setFormValues((prev) => ({ ...prev, cities: value }));
              setErrors((prev) => ({ ...prev, cities: "" }));
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "end", p: 2 }}>
        <Button
          onClick={handleAddAgent}
          variant="contained"
          color="primary"
          sx={{ textTransform: "none" }}
        >
          Add Agent
        </Button>
      </DialogActions>
    </Dialog>
  );
};
